import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@uhg-abyss/web/ui/Button';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { useRef } from 'react';

import { phoneOnly } from '../../../../common/ConstantsStyles';
import { handleLinkAndModalTrack } from '../../../../common/Utils/adobeTrackUtils';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { CompareProvider } from '../../../../models/Provider';

type Props = {
  text: string;
  sectionType: string;
  setOpenShare: (a: boolean) => void;
  openShare?: boolean;
  openCompare?: boolean;
  setOpenCompare: (a: boolean) => void;
  setSelectedItems: (a: CompareProvider[]) => void;
  setSelectedCheckbox: (a: { checked: {} }) => void;
  setIsClicked: React.Dispatch<React.SetStateAction<boolean>>;
  isShareButton?: boolean;
  view: String;
  enableMapViewEnhancements: boolean;
};

export const ShareCompareButtonStyled = styled(Button, {
  variants: {
    view: {
      desktop: {
        '&.abyss-button-root': {
          paddingLeft: 10,
          paddingRight: 15,
        },
      },
      enhancement: {
        '&.abyss-button-root': {
          paddingLeft: 0,
          paddingRight: 16,
          '&:hover,&:active': {
            '> span': {
              backgroundColor: '$core.color.brand.100',
            },
            backgroundColor: '$core.color.brand.100',
          },
        },
      },
    },
    color: {
      primary: {
        '&:hover,&:active': {
          '> span': {
            backgroundColor: '$core.color.neutral.30',
          },
          backgroundColor: '$core.color.neutral.30',
        },
        backgroundColor: '$core.color.brand.100',
      },
      nocolor: { backgroundColor: 'unset' },
    },
  },
});

export const ShareCompareButton = ({
  text,
  setOpenShare,
  openShare,
  openCompare,
  sectionType,
  setOpenCompare,
  setSelectedCheckbox,
  setSelectedItems,
  isShareButton = true,
  setIsClicked,
  enableMapViewEnhancements,
  view,
}: Props) => {
  const mobileScreen = useMediaQuery(phoneOnly);
  const isOpen = openShare || openCompare;
  const textColor = isOpen ? '#EEF8FB' : '$core.color.brand.60';

  const setUIstate = useStore(StoreKeys.SET_UI_STATE);
  const uiState = useStore(StoreKeys.UI_STATE);

  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleAdobeButtonClickTrack = (buttonName) => {
    const linkName = `${buttonName} button`;
    const location = `body:${sectionType} results`;
    const modalName: string = `${buttonName} drawer`;
    handleLinkAndModalTrack(linkName, location, modalName);
  };

  const handleButton = () => {
    setUIstate({
      ...uiState,
      shareProvidersFlow: {
        indexOflastProviderSelected: 0,
        shouldFocusLastIndex: true,
      },
      compareProvidersFlow: {
        indexOflastProviderSelected: 0,
        shouldFocusLastIndex: true,
      },
    });

    if (isShareButton) {
      handleAdobeButtonClickTrack('share');
      setOpenShare(!openShare);
      setOpenCompare(false);
      setSelectedCheckbox({ checked: {} });
      setSelectedItems([]);
    } else {
      handleAdobeButtonClickTrack('compare');
      setOpenCompare(!openCompare);
      setOpenShare(false);
      setSelectedCheckbox({ checked: {} });
      setSelectedItems([]);
    }
    setIsClicked(false);

    if (buttonRef.current) {
      setTimeout(() => {
        buttonRef.current?.focus();
      }, 0);
    }
  };

  return (
    <ShareCompareButtonStyled
      aria-pressed={!!isOpen}
      color={isOpen ? 'primary' : 'nocolor'}
      css={
        mobileScreen &&
        !enableMapViewEnhancements && {
          '&.abyss-button-root': {
            border: 'solid 2px $core.color.brand.100',
            '&:active, &:focus, &:hover': {
              backgroundColor: '#D0F1F5',
            },
          },
        }
      }
      data-auto-testid={`${
        isShareButton ? 'filter-buttons-share' : 'filter-buttons-compare'
      }`}
      data-testid={`${
        isShareButton ? 'filter-buttons-share' : 'filter-buttons-compare'
      }`}
      id="mybutton"
      onClick={handleButton}
      ref={buttonRef}
      size={mobileScreen && !enableMapViewEnhancements ? '$sm' : '$md'}
      variant={mobileScreen && !enableMapViewEnhancements ? 'outline' : 'text'}
      view={view}
    >
      <Layout.Group>
        <IconSymbol
          color={isOpen ? '#EEF8FB' : '$core.color.brand.60'}
          icon={isShareButton ? 'share' : 'compare_arrows'}
        />
        <Text
          color={
            mobileScreen && !enableMapViewEnhancements
              ? '$core.color.brand.100'
              : textColor
          }
          data-auto-testid={`${
            isShareButton ? 'text-buttons-share' : 'text-buttons-compare'
          }`}
          data-testid={`${
            isShareButton ? 'text-buttons-share' : 'text-buttons-compare'
          }`}
          fontWeight={
            mobileScreen && !enableMapViewEnhancements ? 'medium' : 'regular'
          }
        >
          {text}
        </Text>
      </Layout.Group>
    </ShareCompareButtonStyled>
  );
};
